<style lang="scss">
@import "../styles/payment-v2/payment-menu-content-form.scss";
@import "@/styles/common.scss";
@import "@/styles/_mixins.scss";
.form-offset-right {
  padding-right: 0;
  @include tablet {
    padding-right: 16px;
  }
}
</style>

<template>
  <form v-if="isVisible" action="/charge" method="post" id="payment-form" @submit.prevent="onClickSubmitPayment">
    <v-layout row wrap>
      <v-flex v-if="!new_payment_ui && !isRenewalPayment" xs12 md6 class="form-offset-right">
        <PaymentMenuContentSummary
          :subscription-ids="subscriptionIds"
          :default-payment-data="defaultPaymentData"
          :one-off-codes="oneOffCodes"
          :request-payment-items="requestPaymentItems"
          :add-shareholder-id="addShareholderId"
          :promo-code-enabled="promoCodeEnabled"
          :upgrade-corp-sec-subscription="upgradeCorpSecSubscription"
          :company-id-from-billing-profile="companyIdFromBillingProfile"
        />
      </v-flex>
      <v-flex
        :class="isRenewalPayment || isMobile ? 'xs12 md12' : 'xs6 md6'"
        class="payment-menu-content-form-left-panel-v2 new-payment-ui-method"
      >
        <div class="mb-3">
          <div class="new-payment-ui-method-title">
            {{ this.$t("$onboarding.content_paragraph.payment.summary.mode_of_payment.title") }}
          </div>
          <PaymentMethodSelect :payment-methods-topic="paymentMethodsTopic" paymentMethodSelected="add_card" />
        </div>
        <div>
          <v-layout row wrap>
            <v-flex xs12>
              <CardSelectField
                :selected-card="selectedCard"
                :payment-methods-topic="paymentMethodsTopic"
                :credit-cards="creditCards"
              />
            </v-flex>
            <v-flex xs12 class="mt-3">
              <v-text-field
                :label="$t('$onboarding.fields.placeholders.name_on_card')"
                outline
                :class="[
                  inputClass,
                  { focused: isNameOnCardInputFocused },
                  { empty: isNameOnCardInputEmpty },
                  { invalid: isNameOnCardInputInvalid },
                  `payment-text-field`
                ]"
                :rules="[nameOnCardInputErrorMessage]"
                :error-message="[nameOnCardInputErrorMessage]"
                required
                @focus="isNameOnCardInputFocused = !isNameOnCardInputFocused"
                @blur="isNameOnCardInputFocused = !isNameOnCardInputFocused"
                id="nameOnCard"
                v-model="nameOnCard"
              ></v-text-field>
            </v-flex>
            <v-flex xs12>
              <div class="field">
                <div id="cardNumber" ref="cardNumber" :class="[inputClass]" />
                <label class="paymentFormInputLabel" for="cardNumber">{{
                  $t("$onboarding.fields.placeholders.card_number")
                }}</label>
                <div class="error-message-hint" ref="cardNumberErrors" role="alert"></div>
              </div>
            </v-flex>
            <v-flex xs4>
              <div class="field mt-3">
                <div id="cardCvc" ref="cardCvc" :class="[inputClass]" />
                <label class="paymentFormInputLabel" for="cardCvc">{{
                  $t("$onboarding.fields.placeholders.cvc")
                }}</label>
                <div class="error-message-hint cvc" ref="cardCvcErrors" role="alert"></div>
              </div>
            </v-flex>
            <v-flex xs8 pl-2>
              <div class="field mt-3">
                <div id="cardExpiry" ref="cardExpiry" :class="[inputClass]" />
                <label class="paymentFormInputLabel" for="cardExpiry">{{
                  $t("$onboarding.fields.placeholders.expiry")
                }}</label>
                <div class="error-message-hint" ref="cardExpiryErrors" role="alert"></div>
              </div>
            </v-flex>
            <img src="/imgs/powered-by-stripe.png" class="powered-by-stripe-image" />
          </v-layout>
        </div>
        <div class="new-payment-ui-method-actions">
          <v-layout>
            <v-flex>
              <v-btn v-if="hasBackButtonListener" color="primary" outline type="button" @click="onBackEvent()"
                ><v-icon left dark>keyboard_arrow_left</v-icon>{{ this.$t("$onboarding.card.buttons.back") }}</v-btn
              >
            </v-flex>
            <v-flex shrink>
              <v-btn color="primary" :loading="isButtonLoading" :disabled="isButtonDisabled" type="submit"
                ><span class="text-uppercase font-weight-bold">{{
                  this.$t("$onboarding.card.buttons.pay_now")
                }}</span></v-btn
              >
            </v-flex>
          </v-layout>
        </div>
      </v-flex>
      <v-flex xs12>
        <div v-if="isPartnerPayment" class="text-xs-right mt-2">
          <br />
          <div class="hidden-md-and-down">
            <v-btn color="primary" type="button" @click="onBackClick(onBoardingTopic)">
              <v-icon left dark>keyboard_arrow_left</v-icon>
              {{ this.$t("$onboarding.card.buttons.back") }}
            </v-btn>
            <v-btn color="primary" :loading="isButtonLoading" :disabled="isButtonDisabled" type="submit">
              {{ this.$t("$onboarding.card.buttons.confirm") }}
              <v-icon right dark>keyboard_arrow_right</v-icon>
            </v-btn>
          </div>
        </div>
      </v-flex>
    </v-layout>
  </form>
</template>

<script>
/* eslint-disable */

// Components
import PaymentMenuContentSummary from "./PaymentMenuContentSummary.vue";
import CardSelectField from "./PaymentMenuContentFormCardSelect.vue";
import PaymentMethodSelect from "./PaymentMenuContentPaymentMethod.vue";
// Proxies
import InsertCreditCardProxy from "@/proxies/back-end/credit-cards/insert";
import PaymentForDraftCompanyProxy from "@/proxies/back-end/credit-card-payment/create-company";
import SubscriptionPaymentPostProxy from "@/proxies/back-end/subscriptions/payment";
import PaymentTokensProxy from "@/proxies/back-end/payment-tokens/create";
import UpdateSubscriptionProxy from "@/proxies/back-end/subscriptions/update";
import UpdatePaymentRequestByTokenProxy from "@/proxies/back-end/payment-request/update";
import addNewShareholder from "@/proxies/back-end/workflow/add-new-shareholder/add-new-shareholder";
import GetCompanyProxy from "@/proxies/back-end/company/get";

// Constants
import { Constants } from "customer-sdk/constants";

const { WORKFLOW_CONSTANTS } = Constants.WORK_FLOW;
const EVENTS = Constants.EVENTS;
const PAYMENT_METHODS_EVENTS = Constants.ON_BOARDING_PAYMENT_METHODS_EVENTS;
const ON_BOARDING_EVENTS = Constants.ON_BOARDING_EVENTS;
const { COMPANY_USER_MODULE, COMPANY_USERS } = Constants.COMPANY_USERS_CONSTANTS;
const CARD_SETUP_CONSTANTS = Constants.CARD_SETUP;
const PAGE_URLS = Constants.PAGE_URLS;

// Mixins
import { AddNewCardMixin } from "@/mixins/addNewCard";
import { GoogleAnalyticsMixin } from "@/mixins/googleAnalytics";
import LocalStoreManager from "@/utilities/local-store-manager";
import ShoppingCartManager from "@/utilities/shopping-cart-manager";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
// Functions
import { validateCardHolderNameField } from "@/mixins/addNewCard";
// Utilities
import GlobalTopic from "@/utilities/global-topic";
import AllTypePaymentPostProxy from "@/proxies/back-end/subscriptions/payment-all-type";
import { handleTask } from "@/utilities/workflow-task-handler";
import { setupStripe } from "@/utilities/setup-stripe";
import { get, pickBy, isEmpty } from "lodash";

//#region private variables
let POST_PAYMENT_PATH = PAGE_URLS.INCORPORATION;
let store = new LocalStoreManager();
//#endregion

//#region private methods
let addCreditCard = async component => {
  try {
    let token = await registerCardToStripe(component);
    if (token) {
      let proxy = new InsertCreditCardProxy();
      let payload = {
        token: token.id,
        company: new LocalStoreManager().getCompanyId()
      };
      let response = await proxy.execute(component.defaultPaymentData, payload);
      return response;
    }
    return false;
  } catch (error) {
    if (error && error.message) {
      publishSubmitPaymentError(component, error.message);
    }
    throw error;
  }
};

let registerCardToStripe = async component => {
  try {
    validateCardHolderNameField(component);
    const { token, error } = await component.stripe.createToken(component.card, { name: component.nameOnCard });

    if (error) {
      let errorCode = error.code;
      let errorMessage;

      switch (errorCode) {
        case "card_declined":
          errorMessage = "Your card has been declined.";
          break;
        case "expired_card":
          errorMessage = "Your card has been expired.";
          break;
        case "incorrect_cvc":
          errorMessage = "Incorrect CVC.";
          break;
        default:
          errorMessage = error.message;
          break;
      }

      GlobalTopic.publishChange({
        type: EVENTS.ON_OPEN_SNACKBAR,
        data: {
          snackbarText: errorMessage,
          timeout: 4000
        }
      });
      return false;
    }

    return token;
  } catch (error) {
    throw error;
  }
};

let handlePayment = async (component, creditCardId) => {
  try {
    if (component.subscriptionIds && component.oneOffCodes && !component.requestPaymentId) {
      let paymentData = await handleSubscriptionAndOneOffPayment(component, creditCardId);
      component.parseGAData(paymentData);
      if (component.task) {
        try {
          await handleTask(component.task, paymentData, WORKFLOW_CONSTANTS.PAYMENT_STATUS.PAID);
        } catch (error) {
          console.log("error", error);
        }
      } else {
        if (component.accountingSetupOptions) {
          const { redirect, openedPage, questionnaire_token, email } = component.accountingSetupOptions;
          await component.updateQuestionnaireMultiCurrency(openedPage);
          const urlParams = new URLSearchParams(pickBy({ redirect, openedPage, questionnaire_token, email }));
          component.$router.push(
            `${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${
              CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE
            }&${urlParams.toString()}`
          );
        } else {
          component.$router.push(
            `${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`
          );
        }
      }
    } else if (!isEmpty(component.unpaidBillsDetails)) {
      try {
        let paymentData = await handleUnpaidBillPayment(component, creditCardId);
        component.$router.push(`${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`);
      } catch (error) {
        console.log("error", error);
      }
    } else if (component.requestPaymentId) {
      let paymentData = await handleOneOffPaymentFromPaymentRequest(component, creditCardId);
      component.parseGAData(paymentData);
      let proxy = new UpdatePaymentRequestByTokenProxy();

      const request_payment_status = getAppFeatureProp(
        component.platformConfig.cmsGeneralFeatureList,
        "request_payment_status"
      );

      await proxy.updateRecord(component.defaultPaymentData, {
        paymentRequestId: component.requestPaymentId,
        status: request_payment_status.value.USED
      });
      component.$router.push(`${PAGE_URLS.POST_PAYMENT_REQUEST}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`);
    } else if (component.upgradeCorpSecSubscription) {
      const companyUsers = component.$store.getters[`${COMPANY_USER_MODULE}/${COMPANY_USERS}`];
      try {
        let paymentData = await handleUpgradeCorpSecSubscription(component, creditCardId);

        await handleTask(component.task, paymentData, WORKFLOW_CONSTANTS.PAYMENT_STATUS.PAID, companyUsers);
      } catch (error) {
        console.log("error", error);
      }
    } else if (component.addShareholderId) {
      let paymentData = await handleShareholderPayment(component, creditCardId);
      component.parseGAData(paymentData);
      component.$router.push(
        `${PAGE_URLS.POST_REQUEST_SHAREHOLDER}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`
      );
    } else if (typeof component.subscriptionIds != "undefined" && !component.oneOffCodes) {
      let paymentData = await handleSubscriptionPayment(component, creditCardId);
      component.parseGAData(paymentData);
      if (component.task) {
        try {
          await handleTask(component.task, paymentData, WORKFLOW_CONSTANTS.PAYMENT_STATUS.PAID);
        } catch (error) {
          console.log("error", error);
        }
      } else {
        if (component.isPartnerBillingProfilePayment) {
          component.$emit("displayAfterPaymentScreen", { paymentMethod: "creditCard" });
        } else {
          component.$router.push(
            `${PAGE_URLS.POST_RENEWAL_PAYMENT}?paidWith=${CARD_SETUP_CONSTANTS.CARD_PAYMENT_TYPE}`
          );
        }
      }
    } else if (component.isPartnerPayment) {
      let paymentData = await handleOnboardingPayment(component, creditCardId);
      component.parseGAData(paymentData);
      publishPaymentSubmitted(component);
    } else {
      let paymentData = await handleOnboardingPayment(component, creditCardId);
      component.parseGAData(paymentData);

      const new_payment_page_enabled = getAppFeatureProp(
        component.platformConfig.cmsAppFeatures,
        "new_payment_page_enabled"
      );

      let isNewPaymentPage = new_payment_page_enabled.enabled;
      setTimeout(() => {
        if (isNewPaymentPage) {
          component.$store.dispatch("companyUsersModule/updateCompanyDeleteIcon");
          component.$store.dispatch("companyUsersModule/hideShoppingCartAfterPayment");
          component.$store.dispatch("companyUsersModule/hideShoppingCartDropdown");
          component.$emit("displayAfterPaymentScreen", { paymentMethod: "creditCard" });
        } else {
          component.$router.push(`${POST_PAYMENT_PATH}?isInitial=true`);
        }
      }, 5000);
    }
  } catch (error) {
    console.log("this payment error::", error);
    publishSubmitPaymentError(component, error.message);
    throw error;
  }
};

const handleUpgradeCorpSecSubscription = async (component, creditCardId) => {
  const proxy = new AllTypePaymentPostProxy();
  const company_id = component.defaultPaymentData.id;
  const amendCompanyShareStructureData = localStorage.getItem(`amendCompanyShareStructureData_${company_id}`);
  const shareholders = get(JSON.parse(amendCompanyShareStructureData), "shareholders", []);
  const totalNumberOfShareholder = shareholders.filter(shareholder => !shareholder.is_removed).length;

  try {
    let payload = {
      company_id,
      card_id: creditCardId,
      payment_token: component.paymentToken,
      total_number_of_shareholder: totalNumberOfShareholder
    };
    return await proxy.upgradeCorpSecSubscriptionPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

const handleUnpaidBillPayment = async (component, creditCardId) => {
  const proxy = new AllTypePaymentPostProxy();
  const company_id = component.defaultPaymentData.id;
  try {
    let payload = {
      company_id,
      card_id: creditCardId,
      payment_token: component.paymentToken,
      unpaidBillsDetails: component.unpaidBillsDetails
    };

    return await proxy.unpaidBillPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleSubscriptionAndOneOffPayment = async (component, creditCardId) => {
  try {
    let proxy = new AllTypePaymentPostProxy();
    let payload = {
      card_id: creditCardId,
      companySubscriptionIds: component.subscriptionIds,
      payment_token: component.paymentToken,
      oneOffCodes: component.oneOffCodes,
      companyId: localStorage.getItem("paymentRequestCompanyId")
        ? localStorage.getItem("paymentRequestCompanyId")
        : localStorage.getItem("companyId")
    };
    return await proxy.postPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let buildPayload = async component => {
  const cart = new ShoppingCartManager();
  let response = await cart.fetchUpgradeCorpsecSubscriptionDetails(component.addShareholderId);
  let companyProxy = new GetCompanyProxy();
  let company = await companyProxy.execute({ id: await store.getCompanyId(), authToken: store.getToken() });

  const companyId =
    localStorage.getItem("paymentRequestCompanyId") && localStorage.getItem("paymentRequestCompanyId") != "null"
      ? localStorage.getItem("paymentRequestCompanyId")
      : localStorage.getItem("companyId");
  const payload = {
    authToken: store.getToken(),
    payload: {
      requested_by_user_id: store.getUserId(),
      requested_by_user_name: store.getUserEmail(),
      company_user_id: store.getUserId(),
      company_user_name: store.getUserEmail(),
      company_id: companyId,
      company_name: company ? company.name : "",
      price: response ? response.shoppingCartTotal : 0,
      company_workflow_data: {
        request_information: {
          request_type: "addition_of_shareholder",
          company_id: companyId,
          company_name: company ? company.name : "",
          requested_by_user_id: store.getUserId()
        }
      }
    }
  };
  return payload;
};

let handleShareholderPayment = async (component, creditCardId) => {
  try {
    let proxy = new AllTypePaymentPostProxy();
    let payload = {
      card_id: creditCardId,
      payment_token: component.paymentToken,
      addShareholderId: component.addShareholderId,
      companyId: localStorage.getItem("paymentRequestCompanyId")
        ? localStorage.getItem("paymentRequestCompanyId")
        : localStorage.getItem("companyId")
    };
    // start workflow
    let workflowPayload = await buildPayload(component);
    await addNewShareholder.startAdditionOfShareholder(workflowPayload);

    return await proxy.postUpgradePayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleOneOffPaymentFromPaymentRequest = async (component, creditCardId) => {
  try {
    let proxy = new AllTypePaymentPostProxy();
    let payload = {
      card_id: creditCardId,
      payment_token: component.paymentToken,
      companySubscriptionIds: component.subscriptionIds,
      oneOffCodes: component.oneOffCodes,
      companyId: localStorage.getItem("paymentRequestCompanyId")
        ? localStorage.getItem("paymentRequestCompanyId")
        : localStorage.getItem("companyId"),
      requestPaymentId: component.requestPaymentId,
      requestPaymentItems: component.requestPaymentItems
    };
    return await proxy.postPayment(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleOnboardingPayment = async (component, creditCardId) => {
  try {
    let proxy = new PaymentForDraftCompanyProxy();
    let payload = {
      card_id: creditCardId,
      payment_token: component.paymentToken,
      is_partner_payment: component.isPartnerPayment
    };
    return await proxy.execute(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let handleSubscriptionPayment = async (component, creditCardId) => {
  try {
    let proxy = new SubscriptionPaymentPostProxy();
    let payload = {
      card_id: creditCardId,
      companySubscriptionIds: component.subscriptionIds,
      payment_token: component.paymentToken
    };
    return await proxy.execute(component.defaultPaymentData, payload);
  } catch (error) {
    initCreatePaymentToken(component);
    throw error;
  }
};

let initCreatePaymentToken = async component => {
  try {
    component.paymentToken = "";

    let payload = {
      authToken: store.getToken()
    };
    let result = await PaymentTokensProxy.createPaymentToken(payload);
    component.paymentToken = result.payment_token;
  } catch (error) {
    throw error;
  }
};

let subscribeToTopic = component => {
  if (component.paymentMethodsTopic) {
    component.paymentMethodsTopic.subscribe(msg => {
      if (msg.type === PAYMENT_METHODS_EVENTS.ON_EXISTING_CARD_SELECTED) {
        if (msg.data.selectedCard) component.selectedCard = msg.data.selectedCard;
        component.isVisible = false;
      } else if (msg.type === PAYMENT_METHODS_EVENTS.ON_ADD_NEW_CARD_SELECTED) {
        if (msg.data.selectedCard) component.selectedCard = msg.data.selectedCard;
        component.isVisible = true;
      } else if (msg.type === PAYMENT_METHODS_EVENTS.ON_BANK_TRANSFER_CLICKED) {
        if (msg.data.selectedCard) component.selectedCard = msg.data.selectedCard;
        component.isVisible = false;
      }
    });
  }
};

let publishSubmitPaymentError = (component, message) => {
  let { paymentMethodsTopic } = component;
  if (paymentMethodsTopic) {
    paymentMethodsTopic.publish({
      type: PAYMENT_METHODS_EVENTS.ON_SUBMIT_PAYMENT_ERROR,
      data: message
    });
  }
};

let checkIfComponentShouldLoad = component => {
  let cards = component.creditCards;
  let userDontHaveCard = cards === null || !(cards instanceof Array) || cards.length === 0;

  if (userDontHaveCard) {
    return true;
  } else {
    return cards.length === 1 && cards[0].value === CARD_SETUP_CONSTANTS.ADD_CARD_KEY;
  }
};

let removeStripeFormIfCardIsSelected = component => {
  if (component.selectedCard !== CARD_SETUP_CONSTANTS.ADD_CARD_KEY) {
    component.isStripeSetupFromUpdateDone = false;
    delete component.card;
    delete component.expiry;
    delete component.cvc;
    return true;
  }
  return false;
};

let getComponentData = component => {
  return {
    lastDoneStep: 4
  };
};

let publishPaymentSubmitted = component => {
  if (component.onBoardingTopic) {
    component.onBoardingTopic.publish({
      type: ON_BOARDING_EVENTS.ON_PAYMENT_SUBMITTED,
      data: getComponentData(component)
    });
  }
};
//#endregion
const checkUI = async component => {
  const isPartner = await store.getPartnerId();
  const new_payment_ui = getAppFeatureProp(component.platformConfig.cmsAppFeatures, "new_payment_ui");
  component.new_payment_ui = new_payment_ui.enabled && (!isPartner || isPartner == "null");
};

export default {
  components: {
    PaymentMenuContentSummary,
    CardSelectField,
    PaymentMethodSelect
  },
  mixins: [AddNewCardMixin, GoogleAnalyticsMixin],
  props: {
    onBoardingTopic: Object,
    paymentMethodsTopic: Object,
    defaultPaymentData: Object,
    creditCards: Array,
    subscriptionIds: Array,
    unpaidBillsDetails: Array,
    task: Object,
    oneOffCodes: Array,
    requestPaymentId: String,
    requestPaymentItems: Array,
    platformConfig: Object,
    isPartnerPayment: Boolean,
    addShareholderId: String,
    promoCodeEnabled: Boolean,
    accountingSetupOptions: Object,
    updateQuestionnaireMultiCurrency: Function,
    isPartnerBillingProfilePayment: {
      type: Boolean,
      default: false
    },
    upgradeCorpSecSubscription: { type: Boolean, default: false },
    companyIdFromBillingProfile: String,
    // USED TO SET THE DESIGN BREAKPOINTS CLASS BASED ON PARENT COMPONENT
    isRenewalPayment: Boolean
  },
  data() {
    return {
      isVisible: false,
      selectedCard: CARD_SETUP_CONSTANTS.ADD_CARD_KEY,
      isNameOnCardInputFocused: false,
      inputClass: "paymentFormInput",
      nameOnCard: "",
      isButtonLoading: false,
      nameOnCardInputErrorMessage: null,
      paymentToken: "",
      isStripeSetupFromUpdateDone: false,
      new_payment_ui: false,
      isMobile: window.innerWidth < 960,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    isButtonDisabled() {
      let component = this;

      if (component.paymentToken === "") {
        return true;
      }

      return false;
    },
    hasBackButtonListener() {
      return this.$parent.$listeners && this.$parent.$listeners.triggerBackButton instanceof Function;
    }
  },
  methods: {
    onBackClick: function(onBoardingTopic) {
      if (onBoardingTopic) {
        onBoardingTopic.publish({
          type: ON_BOARDING_EVENTS.ON_CONTACT_SELECTED
        });
      }
    },
    onBackEvent: function() {
      this.$parent.$emit("triggerBackButton");
    },
    onClickSubmitPayment: async function() {
      let component = this;
      try {
        component.isButtonLoading = true;
        let creditCard = await addCreditCard(component);
        if (creditCard) {
          await handlePayment(component, creditCard._id);
        } else {
          component.isButtonLoading = false;
        }
      } catch (error) {
        component.isButtonLoading = false;
        throw error;
      }
    },
    handleResponsive() {
      this.windowWidth = window.innerWidth;
    }
  },
  created() {
    let component = this;
    initCreatePaymentToken(component);
  },
  async mounted() {
    let component = this;
    window.addEventListener("resize", this.handleResponsive);
    subscribeToTopic(component);
    component.isVisible = checkIfComponentShouldLoad(component);

    if (component.isVisible) {
      setupStripe(component);
    }
    await checkUI(component);
  },
  watch: {
    windowWidth(width) {
      this.isMobile = width < 1024;
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResponsive);
  },
  updated() {
    let component = this;
    if (removeStripeFormIfCardIsSelected(component)) return;
    if (component.isButtonLoading) return;
    // setup stripe only if selected payment method/card is not bank transfer
    // and when stripe setup is not done on update of fields
    if (component.selectedCard !== "bank_transfer" && !component.isStripeSetupFromUpdateDone) {
      setupStripe(component);
      component.isStripeSetupFromUpdateDone = true;
    }
  }
};
</script>
